/**
 * PublicPageRouter — Phase 3 §1.
 *
 * Wraps the 7 public route elements (About, Pricing, Map, Social,
 * Terms, Privacy, Contact) and decides per request what chrome to
 * mount:
 *
 *   - Loading window (user === undefined) → render nothing (children
 *     mount uses PublicPageShell internally if user resolves to null).
 *   - Logged out (user === null) → render the children directly.
 *     The page itself imports `<PublicPageShell>` and uses it as its
 *     own wrapper — UNCHANGED from today.
 *   - Suspended (user.status !== "active") → render the children
 *     directly. The PublicPageShell inside the page gives them the
 *     neutral unauth chrome per K3 — never wrap a suspended user in
 *     a role shell.
 *   - Authed + active → wrap the children in the role shell matching
 *     `useAuth().view` (K12). The shell accepts a `children` prop
 *     (Phase 3 §1 K1=B2) and renders {children || <Outlet/>}.
 *
 * When wrapping public children in a role shell, the page's own
 * `<PublicPageShell>` STILL renders inside the shell body — which
 * means we get a doubled nav (the shell's nav + PublicPageShell's
 * SharedNav). To avoid that, the public pages need to keep using
 * `<PublicPageShell>` for the UNAUTH path while we strip the outer
 * `<PublicPageShell>` when an authed user lands here.
 *
 * Strategy: the public page components keep `<PublicPageShell>` as
 * their wrapper. PublicPageRouter detects an authed user, mounts the
 * role shell, and renders the children DIRECTLY (bypassing
 * PublicPageShell). To make this work without forking every page,
 * the page bodies are wrapped here in a `<PublicShellSurround>` that
 * conditionally renders `<PublicPageShell>` based on `data-role-shell-active`.
 *
 * Simpler implementation: a dedicated wrapper component skips the
 * PublicPageShell and lets the page body sit directly inside the
 * role shell. This requires the page bodies to keep working when
 * NOT wrapped by PublicPageShell — they all do, because the body
 * content is plain JSX that doesn't depend on the shell.
 *
 * Approach used here: render the role shell with a `<PageBody>`
 * inner that calls a small helper to extract just the children's
 * inner content. Since we can't introspect the page's content tree
 * generically, we go a different way: the `children` passed to
 * PublicPageRouter is the WHOLE page element (e.g. `<About />`).
 * `<About>` internally renders `<PublicPageShell><section>...</section></PublicPageShell>`.
 * To suppress the inner shell when wrapping in a role shell, we
 * use an "active wrapper" context that PublicPageShell reads — if
 * a parent has flagged "role shell active", PublicPageShell renders
 * just `{children}` (no SharedNav/Footer).
 */
import React, { createContext, useContext } from "react";
import { useAuth } from "@/context/AuthContext";
import AdminShell from "@/components/admin/AdminShell";
import CustomerShell from "@/components/customer/CustomerShell";
import ProviderShell from "@/components/provider/ProviderShell";

// Context the parent uses to tell <PublicPageShell> "you're nested
// inside a role shell — render just children, skip SharedNav+Footer".
const RoleShellActiveContext = createContext(false);

export function useRoleShellActive() {
  return useContext(RoleShellActiveContext);
}

export default function PublicPageRouter({ children }) {
  const { user, view } = useAuth();

  // Loading window — render children directly so the unauth chrome
  // can show. (PublicPageShell handles the brief flash gracefully —
  // it doesn't depend on user state any more.)
  if (user === undefined) return children;
  if (user === null) return children;

  // Suspended users get neutral unauth chrome (K3). Never wrap a
  // suspended user in a role shell — even when they have an active
  // view persisted in localStorage.
  if ((user.status || "active") !== "active") return children;

  // Authed + active — wrap in the role shell matching the active
  // view. Signal to the inner <PublicPageShell> that it should
  // suppress its own SharedNav+SharedFooter (K7 — drop footer
  // inside role shells).
  const wrapped = (
    <RoleShellActiveContext.Provider value={true}>
      {children}
    </RoleShellActiveContext.Provider>
  );

  if (view === "admin") return <AdminShell>{wrapped}</AdminShell>;
  if (view === "provider") return <ProviderShell>{wrapped}</ProviderShell>;
  return <CustomerShell>{wrapped}</CustomerShell>;
}
